import { KeyValuePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, OnDestroy, forwardRef, SimpleChanges, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOptgroup, MatOption } from '@angular/material/core';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { DocumentSubType } from '@model/ftf-api.model';
import { LoggerService } from '@service/logger.service';
import { Subject } from 'rxjs';
import { takeUntil, startWith, map } from 'rxjs/operators';

@Component({
  selector: 'app-doc-sub-type-multiselect',
  templateUrl: './doc-sub-type-multiselect.component.html',
  styleUrls: ['./doc-sub-type-multiselect.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocSubTypeMutiselectComponent),
      multi: true,
    },
  ],
  imports: [
    MatFormField,
    MatInput,
    FormsModule,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOptgroup,
    MatOption,
    MatCheckbox,
    MatIcon,
    KeyValuePipe,
  ],
})
export class DocSubTypeMutiselectComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  private loggerService = inject(LoggerService);

  @Input() subtypes: Record<string, DocumentSubType[]> = {};
  @Input() grouped = true;
  @Input() placeholder = 'Select items';
  @Input() panelWidth = '600px';

  // Efficient data structures for lookups
  flatSubtypes: DocumentSubType[] = [];
  filteredFlat: DocumentSubType[] = [];
  subtypeMap = new Map<number, DocumentSubType>();
  groupedData: { [key: string]: DocumentSubType[] } = {};

  searchControl = new FormControl('');
  filteredGroups: { [key: string]: DocumentSubType[] } = {};
  selectedValues: number[] = [];

  private destroy$ = new Subject<void>();
  private onChange: (value: number[]) => void = () => {};
  private onTouched: () => void = () => {};

  /**
   * Angular lifecycle hook that is called after the component's data-bound properties have been initialized.
   *
   * - Logs the initialization of the component along with the provided subtypes.
   * - Prepares the data required for the component.
   * - Sets up a subscription to the search control's value changes to filter the data in real-time as the user types.
   * - Ensures the subscription is properly cleaned up when the component is destroyed.
   */
  ngOnInit(): void {
    this.loggerService.debug('[DocSubTypeMultiselect] Component initialized with subtypes:', this.subtypes);
    this.prepareData();

    // Set up filtering
    this.searchControl.valueChanges
      .pipe(
        startWith(''),
        takeUntil(this.destroy$),
        map((value) => {
          this.filterData(value || '');
          return value;
        })
      )
      .subscribe(() => {
        // Filtered data updated in filterData method
      });
  }

  /**
   * Lifecycle hook that is called when any data-bound property of a directive changes.
   * Logs the changes and, if the 'subtypes' input has changed, prepares and filters the data accordingly.
   *
   * @param changes - An object of type `SimpleChanges` that holds the current and previous values of the changed properties.
   */
  ngOnChanges(changes: SimpleChanges): void {
    this.loggerService.debug('[DocSubTypeMultiselect] Input changed:', changes);
    if (changes['subtypes']) {
      this.loggerService.debug('[DocSubTypeMultiselect] New subtypes:', this.subtypes);
      this.prepareData();
      this.filterData(this.searchControl.value || '');
    }
  }

  // Process data into efficient structures - separated from filtering
  /**
   * Prepares and normalizes the subtype data for the DocSubTypeMultiselect component.
   *
   * This method processes the `subtypes` input, which can be either a grouped object or a flat array,
   * and populates internal structures such as `flatSubtypes`, `subtypeMap`, `groupedData`, and filtered lists.
   * It also logs debug and error information using the `loggerService`.
   *
   * - If `grouped` is true and `subtypes` is an object (not an array), it treats the data as grouped,
   *   flattens all items into `flatSubtypes`, assigns group information to each item, and builds a map by item ID.
   * - If `subtypes` is a flat array or an object with a single array property, it extracts the array,
   *   populates `flatSubtypes`, and builds the map.
   * - Initializes filtered lists (`filteredGroups` or `filteredFlat`) based on the data structure.
   * - Clears previous data before processing.
   * - Logs the number of items and map entries prepared.
   *
   * @private
   */
  private prepareData(): void {
    this.loggerService.debug('[DocSubTypeMultiselect] Preparing data...');
    this.loggerService.debug('[DocSubTypeMultiselect] Subtypes:', this.subtypes);

    this.subtypeMap.clear();
    this.flatSubtypes = [];

    if (!this.subtypes) {
      this.loggerService.error('[DocSubTypeMultiselect] No subtypes provided');
      return;
    }

    if (this.grouped && typeof this.subtypes === 'object' && !Array.isArray(this.subtypes)) {
      // Handle grouped data
      this.groupedData = { ...this.subtypes };

      Object.entries(this.subtypes).forEach(([group, items]) => {
        if (Array.isArray(items)) {
          this.flatSubtypes.push(...items);
          items.forEach((item) => {
            if (item && item.id !== undefined) {
              item.group = group; // Store group info
              this.subtypeMap.set(item.id, item);
            }
          });
        }
      });

      // Initialize filtered groups
      this.filteredGroups = { ...this.subtypes };
    } else {
      // Handle flat data - proper handling for array input
      if (typeof this.subtypes === 'object' && !Array.isArray(this.subtypes)) {
        const keys = Object.keys(this.subtypes);
        if (keys.length === 1 && Array.isArray(this.subtypes[keys[0]])) {
          // Extract the array from the single key
          this.loggerService.debug(`[DocSubTypeMultiselect] Found single key '${keys[0]}' with ${this.subtypes[keys[0]].length} items`);

          this.flatSubtypes = [...this.subtypes[keys[0]]];
          this.flatSubtypes.forEach((item) => {
            if (item && item.id !== undefined) {
              this.subtypeMap.set(item.id, item);
            }
          });

          // Initialize filtered flat list
          this.filteredFlat = [...this.flatSubtypes];
        } else {
          this.flatSubtypes = [];
          this.filteredFlat = [];
        }
      }
    }

    this.loggerService.debug(`[DocSubTypeMultiselect] Prepared ${this.flatSubtypes.length} items and ${this.subtypeMap.size} map entries`);
  }

  /**
   * Filters the available document subtypes based on the provided search text.
   * Supports both grouped and flat data structures. When filtering grouped data,
   * only groups with matching items are included in the results. For flat data,
   * only matching items are included. If the search text is empty, all data is restored.
   *
   * @param searchText - The text to filter document subtypes by. Filtering is case-insensitive.
   */
  private filterData(searchText: string): void {
    this.loggerService.debug('[DocSubTypeMultiselect] Filtering with:', searchText);
    const filterValue = searchText.toLowerCase();

    if (this.grouped) {
      // Filter grouped data
      if (!filterValue) {
        this.filteredGroups = { ...this.groupedData };
        return;
      }

      const result: { [key: string]: any[] } = {};

      Object.entries(this.groupedData).forEach(([group, items]) => {
        if (Array.isArray(items)) {
          const filtered = items.filter((item) => item && item.name?.toLowerCase().includes(filterValue));
          if (filtered.length > 0) {
            result[group] = filtered;
            this.loggerService.debug(`[DocSubTypeMultiselect] Group ${group} has ${filtered.length} matches`);
          }
        }
      });

      this.filteredGroups = result;
    } else {
      // Filter flat data
      if (!filterValue) {
        this.filteredFlat = [...this.flatSubtypes];
        return;
      }

      this.filteredFlat = this.flatSubtypes.filter((item) => item && item.name?.toLowerCase().includes(filterValue));
    }
  }

  // Display function using map for lookup - O(1) efficiency
  displayFn(): string {
    return ''; // Always return empty for multi-select
  }

  // Check if an item is selected
  isSelected(id: number | undefined): boolean {
    if (id === undefined) return false;
    return this.selectedValues.includes(id);
  }

  /**
   * Toggles the selection state of a given item by its ID.
   * If the item is already selected, it will be removed from the selection;
   * otherwise, it will be added. Triggers change and touch events,
   * and resets the search field without clearing the current selection.
   *
   * @param id - The unique identifier of the item to toggle. If `undefined`, no action is taken.
   */
  toggleSelection(id: number | undefined): void {
    if (id === undefined) return;
    const index = this.selectedValues.indexOf(id);

    if (index > -1) {
      // Remove if already selected
      this.selectedValues.splice(index, 1);
    } else {
      // Add if not selected
      this.selectedValues.push(id);
    }

    this.onChange(this.selectedValues);
    this.onTouched();

    // Reset search field but don't clear selection
    setTimeout(() => {
      this.searchControl.setValue('', { emitEvent: false });
    });
  }

  /**
   * Removes an item from the `selectedValues` array by its ID.
   * If the ID is found, it is removed and the change is propagated via `onChange`.
   * If the ID is `undefined` or not found, no action is taken.
   *
   * @param id - The ID of the item to remove from the selection. If `undefined`, the method returns immediately.
   */
  removeItem(id: number | undefined): void {
    if (id === undefined) return;
    const index = this.selectedValues.indexOf(id);
    if (index > -1) {
      this.selectedValues.splice(index, 1);
      this.onChange(this.selectedValues);
    }
  }

  /**
   * Retrieves the currently selected items based on their IDs.
   *
   * Iterates over the `selectedValues` array, maps each ID to its corresponding subtype object
   * using the `subtypeMap`, and filters out any undefined or null values.
   *
   * @returns {any[]} An array of selected subtype objects.
   */
  getSelectedItems(): any[] {
    return this.selectedValues.map((id) => this.subtypeMap.get(id)).filter(Boolean);
  }

  /**
   * Returns a string representing the current selection state.
   * - If no items are selected, returns the placeholder text.
   * - If one item is selected, returns the name of the selected item.
   * - If multiple items are selected, returns a summary indicating the number of items selected.
   *
   * @returns {string} The text representing the selection.
   */
  getSelectedText(): string {
    const selected = this.getSelectedItems();
    if (selected.length === 0) {
      return this.placeholder;
    } else if (selected.length === 1) {
      return selected[0].name ?? '';
    } else {
      return `${selected.length} items selected`;
    }
  }

  /**
   * Writes the provided array of numbers to the component's selected values.
   * If the input value is not an array, it resets the selected values to an empty array.
   *
   * @param value - An array of numbers representing the selected values.
   */
  writeValue(value: number[]): void {
    this.selectedValues = Array.isArray(value) ? [...value] : [];
  }

  /**
   * Registers a callback function that is called when the value of the component changes.
   * This method is part of the ControlValueAccessor interface, allowing the component to work with Angular forms.
   *
   * @param fn - The callback function to be invoked when the component's value changes.
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.searchControl.disable() : this.searchControl.enable();
  }

  onSelectionChange(event: MatAutocompleteSelectedEvent): void {
    event.option.value && this.toggleSelection(event.option.value);
    setTimeout(() => {
      this.searchControl.setValue(this.searchControl.value);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
